import React, { useEffect, useState } from 'react';
import {
  Siren, Play, Loader2, AlertTriangle, CheckCircle2, Sparkles,
  ArrowRight, X, MessageSquare, ShieldCheck,
} from 'lucide-react';
import apiService, {
  DisruptionEvent, DisruptionProposal, RecoveryPlan, PlanExplanation, DisruptionType,
} from '../services/api';

const TYPE_LABELS: Record<DisruptionType, string> = {
  crew_unavailable: 'Crew unavailable',
  flight_cancelled: 'Flight cancelled',
  flight_delayed: 'Flight delayed',
  station_closure: 'Station closure',
  generic: 'Generic',
};

const today = () => new Date().toISOString().slice(0, 10);

const DisruptionDesk: React.FC = () => {
  const [text, setText] = useState('');
  const [windowStart, setWindowStart] = useState(today());
  const [windowEnd, setWindowEnd] = useState(today());

  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState<DisruptionProposal | null>(null);

  const [recovering, setRecovering] = useState(false);
  const [disruption, setDisruption] = useState<DisruptionEvent | null>(null);
  const [plans, setPlans] = useState<RecoveryPlan[]>([]);
  const [recent, setRecent] = useState<DisruptionEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadRecent = () => {
    apiService.listDisruptions().then(setRecent).catch(() => setRecent([]));
  };
  useEffect(loadRecent, []);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    setProposal(null);
    try {
      const p = await apiService.iropsIntake(text, windowStart, windowEnd);
      setProposal(p);
    } catch (err: any) {
      setError(err?.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleLogAndRecover = async () => {
    if (!proposal) return;
    setRecovering(true);
    setError(null);
    setPlans([]);
    try {
      const event = await apiService.createDisruption({
        disruption_type: proposal.disruption_type,
        description: proposal.description,
        window_start: proposal.window_start,
        window_end: proposal.window_end,
        affected_crew_ids: proposal.affected_crew_ids,
        affected_pairing_ids: proposal.affected_pairing_ids,
        station: proposal.station,
        delay_hours: proposal.delay_hours,
        raw_input: proposal.raw_input,
      });
      setDisruption(event);
      const result = await apiService.recoverDisruption(event.id, { num_plans: 3 });
      setPlans(result.plans);
      setDisruption(result.disruption);
      setProposal(null);
      loadRecent();
    } catch (err: any) {
      setError(err?.message || 'Recovery failed');
    } finally {
      setRecovering(false);
    }
  };

  const openDisruption = async (id: number) => {
    setError(null);
    try {
      const event = await apiService.getDisruption(id);
      setDisruption(event);
      setPlans(event.plans ?? []);
      setProposal(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load disruption');
    }
  };

  const onApproved = (updated: RecoveryPlan) => {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === updated.id ? updated : { ...p, status: p.status === 'proposed' ? 'superseded' : p.status }),
    );
    setDisruption((d) => (d ? { ...d, status: 'resolved' } : d));
    loadRecent();
  };

  const updateProposal = (patch: Partial<DisruptionProposal>) =>
    setProposal((p) => (p ? { ...p, ...patch } : p));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Siren className="h-8 w-8 text-rose-400" />
        <div>
          <h1 className="text-3xl font-bold text-white">Disruption Desk</h1>
          <p className="text-sm text-gray-400">
            IROPS crew recovery : recommend-only. The optimizer proposes legal options; you approve.
          </p>
        </div>
      </div>

      {error && (
        <div className="glass-card border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: recent disruptions */}
        <div className="glass-card p-4 lg:col-span-1 h-fit">
          <h2 className="text-sm font-semibold text-gray-300 mb-3">Recent disruptions</h2>
          <div className="space-y-2">
            {recent.length === 0 && <p className="text-xs text-gray-500">None logged yet.</p>}
            {recent.map((d) => (
              <button
                key={d.id}
                onClick={() => openDisruption(d.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                  disruption?.id === d.id
                    ? 'border-sky-500/50 bg-sky-500/10'
                    : 'border-gray-800 bg-gray-800/30 hover:border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-sky-300">{d.reference}</span>
                  <StatusBadge status={d.status} />
                </div>
                <p className="mt-1 truncate text-gray-300">{d.description || TYPE_LABELS[d.disruption_type]}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Right: intake + proposal */}
        <div className="space-y-6 lg:col-span-2">
          <div className="glass-card p-6 space-y-4">
            <label className="block text-sm font-medium text-gray-300">
              What happened? <span className="text-gray-500">(plain English)</span>
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="e.g. Captain James Holden called in sick for flight BP100 today"
              className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Window start</label>
                <input type="date" value={windowStart} onChange={(e) => setWindowStart(e.target.value)}
                  className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Window end</label>
                <input type="date" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)}
                  className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none" />
              </div>
            </div>
            <button
              onClick={handleAnalyze}
              disabled={analyzing || !text.trim()}
              className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-sky-500 to-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:from-sky-400 hover:to-sky-500 disabled:opacity-50"
            >
              {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {analyzing ? 'Analyzing…' : 'Analyze'}
            </button>
          </div>

          {/* Proposal : editable before committing */}
          {proposal && (
            <div className="glass-card p-6 space-y-4 border border-sky-500/30">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-sky-300">
                  <MessageSquare className="h-4 w-4" /> Proposed disruption
                  <span className="rounded-full bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                    {proposal.source === 'llm' ? 'AI-parsed' : 'auto-detected'}
                  </span>
                </h2>
                <button onClick={() => setProposal(null)} className="text-gray-500 hover:text-gray-300">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Type</label>
                  <select
                    value={proposal.disruption_type}
                    onChange={(e) => updateProposal({ disruption_type: e.target.value as DisruptionType })}
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none"
                  >
                    {Object.entries(TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Affected crew ids</label>
                  <input
                    value={proposal.affected_crew_ids.join(', ')}
                    onChange={(e) => updateProposal({
                      affected_crew_ids: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })}
                    placeholder="e.g. 12, 34"
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Description</label>
                <input
                  value={proposal.description}
                  onChange={(e) => updateProposal({ description: e.target.value })}
                  className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleLogAndRecover}
                  disabled={recovering}
                  className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:from-emerald-400 hover:to-emerald-500 disabled:opacity-50"
                >
                  {recovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {recovering ? 'Generating options…' : 'Log & generate recovery options'}
                </button>
              </div>
            </div>
          )}

          {/* Plans */}
          {disruption && plans.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span className="font-mono text-sky-300">{disruption.reference}</span>
                <StatusBadge status={disruption.status} />
                <span>· {plans.length} recovery option{plans.length === 1 ? '' : 's'}</span>
              </div>
              {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} resolved={disruption.status === 'resolved'} onApproved={onApproved} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    open: 'bg-amber-500/15 text-amber-300',
    recovering: 'bg-sky-500/15 text-sky-300',
    resolved: 'bg-emerald-500/15 text-emerald-300',
    dismissed: 'bg-gray-700/40 text-gray-400',
    proposed: 'bg-sky-500/15 text-sky-300',
    approved: 'bg-emerald-500/15 text-emerald-300',
    superseded: 'bg-gray-700/40 text-gray-500',
    rejected: 'bg-red-500/15 text-red-300',
  };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${map[status] ?? 'bg-gray-700/40 text-gray-400'}`}>{status}</span>;
};

const Chip: React.FC<{ label: string; value: number; danger?: boolean }> = ({ label, value, danger }) => (
  <span className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
    danger && value > 0 ? 'bg-red-500/15 text-red-300' : 'bg-gray-800 text-gray-300'
  }`}>
    {value} {label}
  </span>
);

const PlanCard: React.FC<{ plan: RecoveryPlan; resolved: boolean; onApproved: (p: RecoveryPlan) => void }> = ({
  plan, resolved, onApproved,
}) => {
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState<PlanExplanation | null>(null);
  const [approving, setApproving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleExplain = async () => {
    setExplaining(true);
    try {
      setExplanation(await apiService.explainPlan(plan.id));
    } catch (e: any) {
      setErr(e?.message || 'Explain failed');
    } finally {
      setExplaining(false);
    }
  };

  const handleApprove = async () => {
    if (plan.summary.uncovered_positions > 0 &&
        !window.confirm('This plan leaves positions uncovered. Approve anyway?')) return;
    setApproving(true);
    setErr(null);
    try {
      onApproved(await apiService.approvePlan(plan.id));
    } catch (e: any) {
      setErr(e?.message || 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const isApproved = plan.status === 'approved';
  const isSuperseded = plan.status === 'superseded';

  return (
    <div className={`glass-card p-5 space-y-3 ${isApproved ? 'border border-emerald-500/40' : isSuperseded ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-white">Option {plan.rank + 1}</span>
          <StatusBadge status={plan.status} />
          <span className="text-xs text-gray-500">disruption cost {plan.objective_cost}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip label="uncovered" value={plan.summary.uncovered_positions} danger />
        <Chip label="changes" value={plan.summary.assignments_changed} />
        <Chip label="deadheads" value={plan.summary.deadheads} />
        <Chip label="reserve call-ups" value={plan.summary.reserves_called} />
      </div>

      {plan.changes.length > 0 && (
        <div className="space-y-1">
          {plan.changes.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
              <span className="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">{c.position}</span>
              <span className="font-mono text-xs text-gray-500">#{c.pairing_id}</span>
              {c.kind === 'swap' && (
                <span className="flex items-center gap-1">
                  <span className="text-gray-400 line-through">{c.from_crew}</span>
                  <ArrowRight className="h-3 w-3 text-gray-500" />
                  <span className="text-emerald-300">{c.to_crew}</span>
                </span>
              )}
              {c.kind === 'added' && <span className="text-emerald-300">+ {c.to_crew}</span>}
              {c.kind === 'removed' && <span className="text-red-300 line-through">{c.from_crew}</span>}
            </div>
          ))}
        </div>
      )}

      {plan.uncovered.length > 0 && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-300">
          Uncovered: {plan.uncovered.map((u) => `${u.position} on #${u.pairing_id}`).join(', ')}
        </div>
      )}

      {explanation && (
        <div className="rounded-lg bg-gray-800/50 p-3 text-sm space-y-2">
          <p className="text-gray-200">{explanation.rationale}</p>
          {explanation.crew_message && (
            <pre className="whitespace-pre-wrap rounded bg-gray-900/60 p-2 text-xs text-gray-400">{explanation.crew_message}</pre>
          )}
        </div>
      )}

      {err && <p className="text-xs text-red-400">{err}</p>}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleExplain}
          disabled={explaining}
          className="flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 transition hover:bg-gray-800 disabled:opacity-50"
        >
          {explaining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
          Explain
        </button>
        {!resolved && plan.status === 'proposed' && (
          <button
            onClick={handleApprove}
            disabled={approving}
            className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:from-emerald-400 hover:to-emerald-500 disabled:opacity-50"
          >
            {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            Approve &amp; apply
          </button>
        )}
        {isApproved && (
          <span className="flex items-center gap-1.5 text-sm text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Applied to roster
          </span>
        )}
      </div>
    </div>
  );
};

export default DisruptionDesk;
